import { ButtonGroup, ButtonIcon } from '@statsbomb/kitbag-components';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { visualisationConfigsAtom } from '@/atoms/queries/visualisation';
import { selectedVisualisationAtom } from '@/atoms/visualisation';
import { useEventBucketsFeature } from '@/hooks/featureFlags';
import { ConfigVisualisation } from '@/types/visualisation';
import { snakeToCamel } from '@/utils/queries';

const PITCH_TYPE_ICONS: Partial<Record<ConfigVisualisation['visualisationKey'], 'ShotMap' | 'HeatmapFlame' | 'Apps'>> =
  {
    eventMap: 'ShotMap',
    heatMap: 'HeatmapFlame',
    eventBuckets: 'Apps',
  };

export const PitchTypeToggle = () => {
  const { t } = useTranslation('visualisation');
  const [searchParams, setSearchParams] = useSearchParams();
  const metricVisualisations = useAtomValue(visualisationConfigsAtom);
  const [selectedVis, setSelectedVis] = useAtom(selectedVisualisationAtom);
  const showEventBuckets = useEventBucketsFeature();

  const toggleableVisualisations = metricVisualisations.flatMap(({ visualisationKey }) => {
    const icon = PITCH_TYPE_ICONS[visualisationKey];
    const isAvailable = visualisationKey !== 'eventBuckets' || showEventBuckets;
    return icon && isAvailable ? [{ visualisationKey, icon }] : [];
  });

  if (toggleableVisualisations.length < 2) return null;

  const handleSelect = (visualisationKey: string) => {
    setSearchParams({ ...Object.fromEntries(searchParams), vis: visualisationKey });
    setSelectedVis(metricVisualisations.find(vis => vis.visualisationKey === visualisationKey)!);
  };

  return (
    <ButtonGroup>
      {toggleableVisualisations.map(({ visualisationKey, icon }) => {
        const label = t(snakeToCamel(visualisationKey));
        const isSelected = visualisationKey === selectedVis.visualisationKey;
        return (
          <ButtonIcon
            key={visualisationKey}
            icon={icon}
            size="small"
            variant={isSelected ? 'primary' : 'ghost'}
            ariaPressed={isSelected}
            title={label}
            onClick={() => handleSelect(visualisationKey)}
          >
            {label}
          </ButtonIcon>
        );
      })}
    </ButtonGroup>
  );
};
